/**
 * Hooks para Cuenta Corriente — Fase 7.1
 *
 * Lectura directa contra las vistas SQL (decisión #014 + #030). La RLS
 * heredada protege automáticamente al admin_empresa.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type {
  CuentaCorrienteDetalle,
  CuentaCorrienteResumen,
} from '../types/cuentaCorriente';

export const QK_CTACTE = {
  resumen:  ['cuenta_corriente', 'resumen'] as const,
  detalle:  (empresaId?: string) =>
    ['cuenta_corriente', 'detalle', empresaId ?? 'all'] as const,
};

// ── RESUMEN POR EMPRESA ───────────────────────────────────────────────────────

/**
 * Trae el resumen agregado por empresa.
 * - admin/operador: ve todas (RLS de las vistas/tablas subyacentes lo permite).
 * - admin_empresa: vista subyacente solo expone los datos de su empresa
 *   (la lista efectivamente trae solo 1 fila).
 */
export function useCuentaCorrienteResumen() {
  return useQuery({
    queryKey: QK_CTACTE.resumen,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_cuenta_corriente_resumen')
        .select('*');
      if (error) throw error;
      return (data ?? []) as CuentaCorrienteResumen[];
    },
    // Datos cambian poco — 30s de stale es suficiente
    staleTime: 30_000,
  });
}

// ── DETALLE POR EMPRESA ───────────────────────────────────────────────────────

/**
 * Trae el detalle (una fila por cupón) de una empresa puntual.
 * Si empresaId es undefined, no consulta.
 */
export function useCuentaCorrienteDetalle(empresaId: string | undefined) {
  return useQuery({
    queryKey: QK_CTACTE.detalle(empresaId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_cuenta_corriente_detalle')
        .select('*')
        .eq('empresa_id', empresaId!);
      if (error) throw error;
      return (data ?? []) as CuentaCorrienteDetalle[];
    },
    enabled: !!empresaId,
    staleTime: 30_000,
  });
}
